import { urlSafeTitle } from '@/utils/urls';

import { buildBrowseCategoryPath } from './browsePath';

/** Dimension segments that may follow a category slug in a `/c/` browse path. */
export type BrowseDimensionKey = 'creator' | 'material' | 'origin' | 'style' | 'subject';

/**
 * Item-facets ships a `categoryUrl` per level, but it is not the slug the browse routes
 * canonicalize on: category-api strips punctuation, so "Women's Clothing" arrives as
 * `womens-clothing` where browse expects `women-s-clothing`. The trailing numeric id masks the
 * difference today because the server resolves the category by id and rewrites the slug. Drop the
 * id while keeping `categoryUrl` and the link lands on `/c/womens-clothing/`, an empty noindex
 * keyword page, instead of the category. Slugifying the display name yields the browse slug for
 * both spellings, so these builders deliberately ignore `categoryUrl`.
 */
const toBrowseSlug = (name: string): string => urlSafeTitle(name);

export const getCategoryBrowsePath = (categoryName: string): string =>
    buildBrowseCategoryPath(toBrowseSlug(categoryName), []);

/**
 * Delegates the tail to `buildBrowseCategoryPath` rather than interpolating it, so this inherits the
 * canonical pair ordering and — the reason it matters here — the redundant-creator pruning: a lot in
 * a brand-named category (`Lalique`, `Meissen`) by the same-named creator collapses to `/c/lalique/`
 * instead of the doubled `/c/lalique/creator/lalique/`.
 */
export const getCategoryDimensionBrowsePath = (
    categoryName: string,
    dimension: BrowseDimensionKey,
    valueName: string
): string =>
    buildBrowseCategoryPath(toBrowseSlug(categoryName), [
        { dimensionKey: dimension, valueSlug: toBrowseSlug(valueName) },
    ]);
